import React, {
	createContext,
	Dispatch,
	ReactNode,
	UIEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { GenerateSetlistProps } from '@/components/Layout/components/GenerateSetlist';
import { SongModalPanelProps } from '@/components/Layout/components/SongModalPanel';
import {
	dashboardUrl,
	directMessagesUrl,
	reelsUrl,
	reportsUrl,
	setlistsUrl,
	songsUrl,
} from '@/config/urls';
import { useGetReelFeedQuery } from '@/graphql';
import { debounce } from '@/helpers/debounce';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { Capacitor } from '@capacitor/core';
import { useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router';

import {
	directMessages,
	heartBlack,
	home,
	reel,
	reportsIcon,
	rows,
	songs,
} from '../../assets/icons';
import constants from '../../theme/exportedVariables.module.scss';
import { EAccountDisplayState } from '../AccountPanel/AccountPanel';
import { CopilotHeader } from '../CopilotHeader';
import { AsaphNavigationPanel } from '../shared/NavigationPanel';
import { AsaphNavigationButtonProps } from '../shared/NavigationPanel/AsaphTopNavigationButton';
import { AddSongsConfig } from './components/AddSongs';
import { GenerateSettingsBottomPanelProps } from './components/GenerateSettingsBottomPanel/GenerateSettingsBottomPanel';
import { SonglistModalPanelProps } from './components/SonglistModalPanel';
import styles from './Layout.module.scss';

interface IProps {
	children: ReactNode;
	navigationKeys?: string[];
	initialHideBottomMenuState?: boolean;
}

type AccountOpenState = {
	open: boolean;
	defaultDisplayState?: EAccountDisplayState;
};

export type ILayoutOpenSong = (config: SongModalPanelProps['config']) => void;

export type ILayoutOpenSonglistWithCallback = (
	callbackFn: SonglistModalPanelProps['callbackFn'],
	config?: SonglistModalPanelProps['config'],
) => void;

export type ILayoutOpenSetlistWithCallback = (
	callbackFn: GenerateSetlistProps['callbackFn'],
	config: GenerateSetlistProps['config'],
) => void;

export type BottomCardConfig = {
	title: string;
	text: string;
	component?: React.ReactNode;
	callbackFn?: () => void;
};

export type OpenAddSongsConfig = AddSongsConfig & {
	callbackFn?: (addedSongIds: string[]) => void;
};

export interface ILayoutProps {
	setAccountOpen: Dispatch<AccountOpenState>;
	openSong: ILayoutOpenSong;
	openSonglistWithCallback: ILayoutOpenSonglistWithCallback;
	openSetlistWithCallback: ILayoutOpenSetlistWithCallback;
	openSetlistGenerate: (
		config?: GenerateSettingsBottomPanelProps['config'],
	) => void;
	openAddSongs: (config?: OpenAddSongsConfig) => void;
	openAddCustomSong: () => void;
	openImportCsv: () => void;
	openBottomCard: (config: BottomCardConfig) => void;
	openQuickAddSongs: () => void;
	setHideBottomMenu: Dispatch<boolean>;
	shootFireworks: () => void;
	scrollTop?: number;
}

export const LayoutContext = createContext<ILayoutProps>({
	setAccountOpen: () => null,
	openSong: () => null,
	openSonglistWithCallback: () => null,
	openSetlistWithCallback: () => null,
	openSetlistGenerate: () => null,
	openAddSongs: () => null,
	openAddCustomSong: () => null,
	openImportCsv: () => null,
	openBottomCard: () => null,
	openQuickAddSongs: () => null,
	setHideBottomMenu: () => null,
	shootFireworks: () => null,
	scrollTop: 0,
});

export const Layout: React.FC<IProps> = ({ children }) => {
	const { chatIds, videoChatIds } = useUnreadChats();
	const { isCopilot } = useAuth();
	const { setAccountOpen, openSupportModal, hideBottomMenu } = useLayout();

	const { refetch } = useGetReelFeedQuery();

	const [scrollTop, setScrollTop] = useState<number>(0);
	const { permissions } = usePermissions();
	const scrollbarRef = useRef<HTMLDivElement>(null);

	const tabs: AsaphNavigationButtonProps[] = useMemo(
		() => [
			{
				url: dashboardUrl,
				label: 'Home',
				defaultIcon: home,
				selectedIcon: home,
				key: 'home',
			},
			{
				url: songsUrl,
				label: 'Songs',
				defaultIcon: songs,
				selectedIcon: songs,
				key: 'songs',
			},
			{
				url: setlistsUrl,
				label: 'Setlists',
				defaultIcon: rows,
				selectedIcon: rows,
				key: 'sets',
				style: { fill: 'none' },
			},
			{
				url: reportsUrl,
				label: 'Reports',
				defaultIcon: reportsIcon,
				selectedIcon: reportsIcon,
				key: 'reports',
				desktopOnly: true,
			},
			{
				url: directMessagesUrl,
				label: 'Messages',
				defaultIcon: directMessages,
				selectedIcon: directMessages,
				hasNotification: !!chatIds.length || !!videoChatIds.length,
				key: 'messages',
			},
			{
				url: reelsUrl,
				label: 'Reels',
				defaultIcon: reel,
				selectedIcon: reel,
				key: 'reels',
				onClick: () => {
					refetch();
				},
			},
			{
				url: '',
				onClick: () => {
					openSupportModal();
				},
				hidden: true,
				label: 'Support us',
				defaultIcon: heartBlack,
				selectedIcon: heartBlack,
				key: 'support',
			},
		],
		[chatIds.length, videoChatIds.length, refetch, openSupportModal],
	);

	const onScroll = (e: UIEvent<HTMLElement>) =>
		setScrollTop((e.target as HTMLElement).scrollTop);
	const debouncedOnScroll = debounce(onScroll, 100);

	const location = useLocation();

	useEffect(() => {
		if (scrollbarRef.current == null) {
			return;
		}
		scrollToTop(scrollbarRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, permissions]);

	const isDesktop = useMediaQuery(
		`(min-width: ${constants.sizeSmallDesktop})`,
	);

	return (
		<section className={styles['layout']}>
			{isCopilot && <CopilotHeader />}
			{isDesktop && !isCopilot && (
				<AsaphNavigationPanel
					setAccountOpen={setAccountOpen}
					position="top"
					buttons={tabs}
				/>
			)}
			<div
				ref={scrollbarRef}
				className={styles['content']}
				onScroll={debouncedOnScroll}
			>
				{children}
			</div>
			{!isDesktop && !hideBottomMenu && !isCopilot && (
				<footer className={styles['footer']}>
					<AsaphNavigationPanel
						position="bottom"
						buttons={tabs.filter((tab) => !tab.desktopOnly)}
						setAccountOpen={setAccountOpen}
					/>
				</footer>
			)}
		</section>
	);
};

function scrollToTop(element: HTMLDivElement) {
	if (element) {
		element.scrollTo({ top: 0 });
	}
}
